import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Column, Table, AutoSizer, CellMeasurer, CellMeasurerCache } from 'react-virtualized';
import 'react-virtualized/styles.css';
import VirtualizedTableHeader from './TableHeader';
import buildAddNewRowRow from './buildAddNewRowRow';
import useRowGetter from './rowGetter';
import tableMethods from './tableMethods';
import { getColumnWidths } from './getColumnWidths';
import { v4 as uuid } from 'uuid';
export default function VirtualizedTable(_ref) {
  let {
    headers,
    rows,
    showControls = true,
    onEdit,
    onDelete,
    onSort,
    onFilter,
    onAddRow = null,
    disableDelete,
    isImmutableUnderReview = false,
    headerHeightPx = 80,
    overscanRowCount = 5,
    // how many rows to render in advance
    tableClassName = '',
    textFilteringQueryParamName,
    // required for the ability to send a url with preexisting filters in the query
    headerOverflow = 'hidden'
  } = _ref;
  const [sortKey, setSortKey] = useState(0); // Dummy value to trigger re-rendering of the table on sort (not on filter)
  const {
    t
  } = useTranslation();
  const addNewRowRow = useMemo(() => buildAddNewRowRow({
    isImmutableUnderReview,
    onAddRow,
    t
  }), [isImmutableUnderReview, onAddRow, t]);
  const [textFilters, setTextFilters, mapHeaders] = tableMethods.useTextFiltering(textFilteringQueryParamName);

  // const mapHeaders = headers => [...headers].map(h => ({...h, order: h.order || null }))
  const [tableRows, setTableRows] = useState(onAddRow ? [addNewRowRow, ...rows] : [...rows]);
  const [tableHeaders, setTableHeaders] = useState(mapHeaders(showControls ? [...headers, {
    text: '',
    width: 80
  }] : headers));
  if (tableHeaders.some(h => h.sortable)) {
    if (tableRows.some(r => !r.key)) {
      throw new Error('If the table has sortable headers, its rows must have a key field and this key should be a unique value based on the rendered data.');
    }
  }
  const columnSortFunction = onSort ? onSort : tableMethods.useSortRows({
    tableHeaders,
    tableRows,
    setTableRows
  });
  const filterRowsByText = tableMethods.useTextFilteringMethod({
    tableHeaders,
    setTableHeaders,
    rows,
    setTableRows,
    textFilters,
    setTextFilters,
    onFilter
  });
  useEffect(() => {
    setTableRows(onAddRow ? [addNewRowRow, ...rows] : [...rows]);
    setTableHeaders(mapHeaders(showControls ? [...headers, {
      text: '',
      width: 80
    }] : headers));
  }, [rows, headers, onAddRow, isImmutableUnderReview, t, addNewRowRow, showControls, mapHeaders]);
  const setTableHeadersSortOrder = index => {
    tableHeaders[index].order = tableHeaders[index].order === 'asc' ? 'desc' : 'asc';
    tableHeaders.forEach((h, i) => {
      if (i !== index) {
        h.order = null;
      }
    });
    setTableHeaders([...tableHeaders]);
    return tableHeaders[index].order;
  };
  const cache = new CellMeasurerCache({
    fixedWidth: true,
    minHeight: 20,
    maxHeight: 150
  });
  const rowGetter = useRowGetter({
    tableRows,
    tableShowControls: showControls,
    tableIsImmutableUnderReview: isImmutableUnderReview,
    tableOnDelete: onDelete,
    tableOnEdit: onEdit,
    tableDisableDelete: disableDelete
  });
  const onClickHeader = headerIndex => {
    const tableHeader = tableHeaders[headerIndex];
    setTableHeadersSortOrder(headerIndex);
    if (typeof columnSortFunction === 'function') {
      columnSortFunction(headerIndex, tableHeader.order, tableHeader.text);
    }
    setTableHeaders([...tableHeaders]);
    setSortKey(prevKey => prevKey + 1); // Update sort key to force re-render
  };

  // Put the table inside a container that has some height defined in whatever way.
  // Otherwise the table will collapse.
  return /*#__PURE__*/React.createElement("div", {
    style: {
      height: 'inherit'
    }
  }, /*#__PURE__*/React.createElement("style", null, `
          /* These defaults are added here with classes, so that you can override them for your specific table. */
          / * Be careful when using vertical margins in the data rows, as they are flaky - because of the virtualized table. */.

          .ReactVirtualized__Table__headerRow {
            text-transform: capitalize;
            margin-bottom: 10px;
          }

          .ReactVirtualized__Table__headerColumn {
            color: #666e7e;
            font-weight: 600;
            font-size: 1.05rem;
          }

          /* the below margin left and right are done so that the row bottom margin that we may want to have between rows
            appear as a continuous line */
          .ReactVirtualized__Table__rowColumn:first-of-type {
            margin-left: 0px;
          }

          .ReactVirtualized__Table__rowColumn {
            margin-right: 0px !important;
            height: 100%;
          }

          .Table-Cell-Content {
            padding: 9px;
            font-size: 16px;
            font-weight: 400;
            height: 100%;
            display: flex;
            flex-direction: column;
          }

          /* set height to define margin between rows */
          /* otherwise vertical margins are flaky and may not work properly at all */
          .Table-Cell-Spacer {
            /* simply the color between the rows */
            background-color: white;
            /* margin left negative is required so that the vertical margin between rows is continuous throuugh the whole row - and does not break up where the columns end */
            margin-left: -10px;
            margin-right: -10px;
            margin-top: auto;
          }
          .
        `), /*#__PURE__*/React.createElement(AutoSizer, {
    key: sortKey
  }, _ref2 => {
    let {
      width,
      height
    } = _ref2;
    const allColumnWidths = getColumnWidths({
      totalWidth: width,
      tableHeaders
    });
    return /*#__PURE__*/React.createElement(Table, {
      className: `Ayming-Virtualized-Table ${tableClassName}`,
      width: width,
      height: height,
      headerHeight: headerHeightPx,
      rowHeight: cache.rowHeight,
      rowCount: tableRows.length,
      rowGetter: rowGetter,
      deferredMeasurementCache: cache,
      overscanRowCount: overscanRowCount // Adjust based on your needs but try to keep below 10
    }, tableHeaders.map((header, index) => /*#__PURE__*/React.createElement(Column, {
      columnData: {
        ...header,
        sortDirection: header.order,
        headerIndex: index,
        onClickHeader,
        onFilterByText: filterRowsByText,
        headerHeight: `${headerHeightPx}px`,
        headerOverflow: headerOverflow
      },
      headerRenderer: VirtualizedTableHeader,
      key: index,
      dataKey: index.toString(),
      width: allColumnWidths[index] || allColumnWidths.dynamicColumnWidth //Need to use ColumnMeasurer here
      ,
      label: header.text,
      cellRenderer: _ref3 => {
        let {
          cellData,
          rowIndex,
          columnIndex,
          parent,
          key,
          style
        } = _ref3;
        return /*#__PURE__*/React.createElement(CellMeasurer, {
          cache: cache,
          columnIndex: columnIndex,
          key: key,
          parent: parent,
          rowIndex: rowIndex
        }, /*#__PURE__*/React.createElement("div", {
          className: "Table-Cell-Container",
          style: {
            ...style,
            height: '100%'
          }
        }, /*#__PURE__*/React.createElement("div", {
          className: "Table-Cell-Content"
        }, cellData, /*#__PURE__*/React.createElement("div", {
          className: "Table-Cell-Spacer"
        }))));
      }
    })));
  }));
}